import type { PointCloud } from "../../components";
import type { AsyncReloadable } from "../../components/reloadable";
import type { BabylonState, Entity } from "../../state";

/**
 * Tells React whether any entity's data is loading, at most once per change.
 *
 * Runs in two places. `reloadStaleComponents` calls it right after starting
 * fetches, so a load is reported in the same task that began it, before its
 * fetch hands control back to the browser. React can then show the spinner
 * while the request is out, rather than a frame or more later. The render
 * loop calls it after every frame, which is where a load that finished in the
 * background is first seen to have ended.
 *
 * Only data fetches count: an `AsyncReloadable` in `"loading"`, which is a
 * position, rotation, vector field, model or joint value cache, or a point
 * cloud's log-time index. A synchronous rebuild (`Reloadable`) never enters
 * `"loading"`, data that needs no fetch resolves before this runs, and a
 * component waiting for the next reload poll is still `"stale"`. A point cloud
 * also counts until it has a frame to show; see {@link isPointCloudLoading}.
 *
 * No-op for a manager built without an `onDataLoadingChanged` callback; the
 * ingest preview's manager has none.
 */
export function reportDataLoading(state: BabylonState): void {
  const onDataLoadingChanged = state.context.callbacks.onDataLoadingChanged;
  if (!onDataLoadingChanged) {
    return;
  }
  const isLoading = isAnyEntityLoading(state);
  if (isLoading === (state.sceneConfig.reportedDataLoading ?? false)) {
    return;
  }
  state.sceneConfig.reportedDataLoading = isLoading;
  onDataLoadingChanged(isLoading);
}

function isAnyEntityLoading(state: BabylonState): boolean {
  for (const entity of Object.values(state.entities)) {
    if (isEntityLoading(entity)) {
      return true;
    }
  }
  return false;
}

/** Whether any of the entity's asynchronously loaded components is fetching. */
function isEntityLoading(entity: Entity): boolean {
  const { position, rotation, vectorField, mesh, pointCloud } = entity;
  if (
    isFetching(position?.cache.load) ||
    isFetching(rotation?.cache.load) ||
    isFetching(vectorField?.cache.load) ||
    isFetching(mesh?.load) ||
    isPointCloudLoading(pointCloud)
  ) {
    return true;
  }
  for (const cache of Object.values(mesh?.jointCaches ?? {})) {
    if (isFetching(cache.load)) {
      return true;
    }
  }
  return false;
}

function isFetching(load: AsyncReloadable | undefined): boolean {
  return load?.state === "loading";
}

/**
 * Whether a point cloud is fetching what it needs before it can show anything:
 * its log-time index, or the frame it wants while none is on screen.
 *
 * A cloud's data arrives a frame at a time, all session long, so once a frame
 * is on screen its fetches — the next frame of playback, the frame a scrub
 * lands on, frames ahead — no longer count. They run at message rate, and the
 * panel header draws its spinner straight from this report, so counting them
 * would flicker it on every message.
 *
 * The wanted frame counts from the moment the index lands, not only once its
 * fetch starts: that is up to a reload poll later, and leaving the gap out
 * would turn the spinner off and on again between the two. A frame the cache
 * holds, or one waiting to be uploaded, goes up on the next poll with no
 * fetch, so it does not count; nor does a cloud whose index load failed,
 * which fetches nothing more.
 */
function isPointCloudLoading(cloud: PointCloud | undefined): boolean {
  if (!cloud) {
    return false;
  }
  if (isFetching(cloud.index.load)) {
    return true;
  }
  return (
    cloud.index.load.state === "loaded" &&
    cloud.wanted !== undefined &&
    cloud.current === undefined &&
    cloud.pending === undefined &&
    !cloud.cache.has(cloud.wanted)
  );
}
